/**
 * Unified ID Generation for Lists and Itineraries
 * 
 * This utility provides consistent ID generation across all creation flows to ensure
 * drag-and-drop, delete, re-order, and other operations work correctly.
 * 
 * ID Format Strategy:
 * - Use existing identifier (_id, postLocationId, location._id) if available (for persisted items)
 * - Otherwise, generate simple counter-based temp IDs: place-1, place-2, etc.
 * 
 * Key Principle: itinerarySlot._id should match timelinePlace.id (or be matchable via fallback)
 */

// Session-scoped counters (reset on page load)
let tempPlaceCounter = 0;      // For itinerary places
let tempListSlotCounter = 0;   // For list slots

/**
 * Generate a temporary ID for itinerary places during editing session
 * 
 * Priority order:
 * 1. Use existing identifier if provided (_id, postLocationId, etc.) - for persisted items from backend
 * 2. Generate simple counter-based format: place-1, place-2, etc.
 * 
 * @param existingId - Existing ID from backend (MongoDB ObjectId) - highest priority
 * @returns Generated ID string
 */
export function generateTempPlaceId(existingId?: string | null): string {
  // Priority 1: Use existing ID if provided (for persisted items from backend)
  if (existingId) {
    return existingId;
  }
  
  // Priority 2: Generate simple counter-based temp ID
  tempPlaceCounter++;
  return `place-${tempPlaceCounter}`;
}

/**
 * Generate a temporary ID for list slots during editing session
 * 
 * Priority order:
 * 1. Use existing identifier if provided (_id, postLocationId) - for persisted items from backend
 * 2. Generate simple counter-based format: place-1, place-2, etc.
 * 
 * @param existingId - Existing ID from backend (MongoDB ObjectId) - highest priority
 * @returns Generated ID string
 */
export function generateTempListSlotId(existingId?: string | null): string {
  // Priority 1: Use existing ID if provided (for persisted items from backend)
  if (existingId) {
    return existingId;
  }
  
  // Priority 2: Generate simple counter-based temp ID
  tempListSlotCounter++;
  return `place-${tempListSlotCounter}`;
}

/**
 * Generate a unique container ID for day containers (session-only, NOT saved to backend)
 * Used for droppable IDs in drag-and-drop operations
 * 
 * @returns Generated container ID string
 */
export function generateDayContainerId(): string {
  // Generate unique ID for day container (session-only, NOT saved to backend)
  // Used for droppable IDs in drag-and-drop
  return `day-container-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * @deprecated Use generateTempPlaceId() instead
 * Legacy function for backward compatibility during migration
 */
export function generateItineraryPlaceId(options: {
  dayIndex: number;
  sequenceIndex: number;
  existingId?: string | null;
  identifier?: string | null;
}): string {
  return generateTempPlaceId(options.existingId ?? null);
}

/**
 * @deprecated Use generateTempListSlotId() instead
 * Legacy function for backward compatibility during migration
 */
export function generateListSlotId(options: {
  sequenceIndex: number;
  existingId?: string | null;
}): string {
  return generateTempListSlotId(options.existingId ?? null);
}

/**
 * Extract identifier from location/slot data for ID generation
 * 
 * Priority order for itinerary places:
 * 1. location._id (existing Location document)
 * 2. placeId / google_place_id
 * 3. locationData._id
 * 4. locationData.id
 * 
 * @param data - Location or slot data
 * @returns Identifier string or null
 */
export function extractLocationIdentifier(data: {
  _id?: string | null;
  placeId?: string | null;
  google_place_id?: string | null;
  locationData?: {
    _id?: string | null;
    id?: string | null;
    google_place_id?: string | null;
  } | null;
  [key: string]: unknown;
}): string | null {
  if (data._id) return data._id;
  if (data.placeId) return data.placeId;
  if (data.google_place_id) return data.google_place_id;
  if (data.locationData?._id) return data.locationData._id;
  if (data.locationData?.id) return data.locationData.id;
  if (data.locationData?.google_place_id) return data.locationData.google_place_id;
  return null;
}

/**
 * Returns true when `id` is a 24-character lowercase/uppercase hex string (MongoDB ObjectId format).
 * Use this instead of inlining the regex in multiple files.
 */
export function isMongoObjectIdString(id: unknown): id is string {
  return typeof id === 'string' && /^[0-9a-fA-F]{24}$/.test(id);
}

/**
 * Returns the value when it is a valid Mongo ObjectId string; otherwise null.
 * Use before sending ids to backend fields typed as ObjectId (e.g. source_social_media_post_id).
 */
export function mongoObjectIdStringOrNull(id: unknown): string | null {
  return isMongoObjectIdString(id) ? id : null;
}

/**
 * Extract identifier from list slot data for ID generation
 *
 * Priority order:
 * 1. _id (existing PostLocation _id when hydrated from the current post)
 * 2. postLocationId
 *
 * Do NOT use locationData._id here: that is the Location document id from search/social APIs.
 * Using it as slot._id caused listBuilder to send it as post_location._id; adding the same
 * place twice then produced duplicate post_location _ids in one save (Mongo E11000).
 *
 * @param slot - List slot data
 * @returns Identifier string or null
 */
export function extractListSlotIdentifier(slot: {
  _id?: string | null;
  postLocationId?: string | null;
  locationData?: {
    _id?: string | null;
  } | null;
  [key: string]: unknown;
}): string | null {
  if (slot._id) return slot._id;
  if (slot.postLocationId) return slot.postLocationId;
  return null;
}

